import { useEffect, useRef, useState } from "preact/hooks";
import { Button } from "../../components";
import { coachingNote } from "../../engine/coaching";
import type { Reward } from "../../engine/rewards";
import { shareSessionCard } from "../../lib/share";
import { profile } from "../../state/store";
import type { SummaryRow } from "../useSession";

type SessionCardProps = {
  clarityEarned: number;
  summary: SummaryRow[];
  rewards: Reward[];
  waveform: number[];
  onDone: () => void;
};

export function SessionCard({ clarityEarned, summary, rewards, waveform, onDone }: SessionCardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [shareState, setShareState] = useState<"idle" | "busy" | "done">("idle");
  const notable = rewards.filter((r) => r.tier >= 2);
  const note = coachingNote(profile.value, clarityEarned);

  useEffect(() => {
    const canvas = canvasRef.current;
    const g = canvas?.getContext("2d");
    if (!canvas || !g) return;
    const width = canvas.width;
    const height = canvas.height;
    const wf = waveform.length ? waveform : [1];
    const max = Math.max(...wf, 1);
    const mid = height / 2;
    const stepX = width / Math.max(1, wf.length - 1);
    let frame = 0;
    let raf = 0;
    const totalFrames = 40;

    function draw() {
      g!.clearRect(0, 0, width, height);
      g!.strokeStyle = "#D6F24B";
      g!.lineWidth = 2;
      g!.beginPath();
      const visible = Math.max(1, Math.round((frame / totalFrames) * wf.length));
      for (let i = 0; i < visible; i++) {
        const dir = i % 2 === 0 ? 1 : -1;
        const x = i * stepX;
        const y = mid - (wf[i]! / max) * (mid - 6) * dir;
        if (i === 0) g!.moveTo(x, y);
        else g!.lineTo(x, y);
      }
      g!.stroke();
      if (frame < totalFrames) {
        frame += 1;
        raf = requestAnimationFrame(draw);
      }
    }
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [waveform]);

  async function handleShare() {
    setShareState("busy");
    await shareSessionCard({
      clarity: clarityEarned,
      durationSec: 0,
      itemsCleared: summary.length,
      waveform,
      note,
    });
    setShareState("done");
  }

  return (
    <div class="stage stage-summary">
      <p class="stage-kicker">Session complete</p>
      <canvas ref={canvasRef} width={320} height={100} class="session-waveform" aria-hidden="true" />
      <div class="display stage-hero-sm">+{clarityEarned} Clarity</div>
      <p class="stage-detail session-note">{note}</p>
      <ul class="summary-list">
        {summary.map((row, i) => (
          <li key={i}>
            {row.label}
            {row.detail ? ` — ${row.detail}` : ""}
          </li>
        ))}
      </ul>
      {notable.length > 0 && (
        <ul class="summary-rewards mono">
          {notable.map((r, i) => (
            <li key={i}>{r.title}</li>
          ))}
        </ul>
      )}
      <div class="summary-actions">
        <Button size="lg" onClick={onDone}>
          Return to the dojo
        </Button>
        <Button size="lg" variant="ghost" onClick={handleShare} disabled={shareState === "busy"}>
          {shareState === "done" ? "Shared" : "Share"}
        </Button>
      </div>
    </div>
  );
}
